import { Edit, AccessTime as TimeIcon } from "@mui/icons-material";
import {
  Box,
  FormControlLabel,
  Grid,
  InputAdornment,
  styled,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
  Badge,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";

const BRAND_ORANGE = "#FF8A00";
const BRAND_CREAM = "#FFFBF5";

const formatTimeInput = (value, previousValue = "") => {
  const digits = value.replace(/\D/g, "").slice(0, 4);

  if (!digits) {
    return "";
  }

  // Primer dígito de hora: solo 0,1,2
  if (digits.length === 1) {
    if (Number(digits[0]) > 2) {
      return previousValue;
    }

    return digits;
  }

  const hourText = digits.slice(0, 2);
  const hours = Number(hourText);

  if (hours > 23) {
    return previousValue;
  }

  if (digits.length === 2) {
    return hourText;
  }

  const minuteDigits = digits.slice(2);

  if (minuteDigits.length === 1 && Number(minuteDigits[0]) > 5) {
    return `${hourText}:`;
  }

  if (minuteDigits.length === 2) {
    const minutes = Number(minuteDigits);

    if (minutes > 59) {
      return `${hourText}:${minuteDigits[0]}`;
    }
  }

  return `${hourText}:${minuteDigits}`;
};

const defaultHours = {
  Monday: { isOpen: false, from: "09:00", to: "17:00" },
  Tuesday: { isOpen: false, from: "09:00", to: "17:00" },
  Wednesday: { isOpen: false, from: "09:00", to: "17:00" },
  Thursday: { isOpen: false, from: "09:00", to: "17:00" },
  Friday: { isOpen: false, from: "09:00", to: "17:00" },
  Saturday: { isOpen: false, from: "09:00", to: "17:00" },
  Sunday: { isOpen: false, from: "09:00", to: "17:00" },
};

const StyledTextField = styled(TextField)(({ theme, disabled }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: disabled ? "transparent" : "#fff",
    transition: "all 0.2s ease-in-out",
    "&:hover": { backgroundColor: BRAND_CREAM },
    "&.Mui-focused": { backgroundColor: "var(--fx-surface-base, #FFFFFF)" },
  },
  "& .MuiInputBase-input": {
    fontSize: { xs: "0.9rem", md: "1.1rem" },
    padding: { xs: "10px 12px", md: "14px 16px" },
    fontWeight: 500,
  },
  "& .MuiInputLabel-root": {
    fontSize: { xs: "0.85rem", md: "1rem" },
    color: theme.palette.text.secondary,
  },
}));

const StyledSwitch = styled(Switch)(() => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: BRAND_ORANGE,
    "& + .MuiSwitch-track": {
      backgroundColor: BRAND_ORANGE,
    },
  },
}));

const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(":").map(Number);

  return hours * 60 + minutes;
};
const getStartTimeError = (value) => {
  if (!value) {
    return "Start time is required";
  }

  const [hours, minutes] = value.split(":");

  if (!hours || !minutes) {
    return "Use HH:mm format";
  }

  const hourNumber = Number(hours);
  const minuteNumber = Number(minutes);

  if (Number.isNaN(hourNumber) || Number.isNaN(minuteNumber)) {
    return "Only numbers are allowed";
  }

  if (hourNumber > 23) {
    return "Hours must be between 00 and 23";
  }

  if (minuteNumber > 59) {
    return "Minutes must be between 00 and 59";
  }

  return "";
};
const getEndTimeError = (startTime, endTime) => {
  if (!endTime) {
    return "End time is required";
  }

  const [hours, minutes] = endTime.split(":");

  if (!hours || !minutes) {
    return "Use HH:mm format";
  }

  const hourNumber = Number(hours);
  const minuteNumber = Number(minutes);

  if (Number.isNaN(hourNumber) || Number.isNaN(minuteNumber)) {
    return "Only numbers are allowed";
  }

  if (hourNumber > 23) {
    return "Hours must be between 00 and 23";
  }

  if (minuteNumber > 59) {
    return "Minutes must be between 00 and 59";
  }

  if (startTime.includes(":") && timeToMinutes(endTime) <= timeToMinutes(startTime)) {
    return "End time must be later than start time";
  }

  return "";
};

const TimeSelector = ({ day, isOpen, onChange, fromTime, toTime, isSmallScreen }) => {
  const isMobile = useMediaQuery("(max-width:600px)");

  const handleTextChange = (e, type) => {
    const currentValue = type === "from" ? fromTime : toTime;

    const formatted = formatTimeInput(e.target.value, currentValue);

    onChange(day, type, {
      target: {
        value: formatted,
      },
    });
  };

  const handleNativeChange = (e, type) => {
    onChange(day, type, {
      target: {
        value: e.target.value,
      },
    });
  };

  const openPicker = (event) => {
    const wrapper = event.currentTarget.closest("[data-time-container]");

    const input = wrapper?.querySelector('input[type="time"]');

    if (input && typeof input.showPicker === "function") {
      input.showPicker();
    } else {
      input?.focus();
      input?.click();
    }
  };

  const fromError = getStartTimeError(fromTime);

  const toError = getEndTimeError(fromTime, toTime);

  return (
    <Box
      sx={{
        width: "100%",
        mb: 2,
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Box>
            <Box display="flex" alignItems="center">
              <StyledSwitch checked={isOpen} onChange={(e) => onChange(day, "isOpen", e)} />

              <Typography
                sx={{
                  ml: 1,
                  fontWeight: isOpen ? 700 : 500,
                }}
              >
                {day}
              </Typography>
            </Box>
            <Box
              sx={{
                mt: 0.5,

                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 10,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  bgcolor: isOpen ? "var(--fx-surface-muted, #F1F5F9)" : "var(--fx-colors-danger-50, #FEF2F2)",
                  color: isOpen ? "#2E7D32" : "#C62828",
                }}
              >
                {isOpen ? "Open" : "Closed"}
              </Box>

              <Typography variant="caption" color="text.secondary">
                {isOpen ? `${fromTime} - ${toTime}` : "Turn on to edit hours"}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Box
            data-time-container
            sx={{
              position: "relative",
            }}
          >
            <StyledTextField
              fullWidth
              disabled={!isOpen}
              label="Start time"
              placeholder="09:00"
              value={fromTime}
              error={isOpen && Boolean(fromError)}
              helperText={isOpen ? fromError || " " : " "}
              onChange={(e) => handleTextChange(e, "from")}
              inputProps={{
                maxLength: 5,
                inputMode: "numeric",
              }}
              InputProps={{
                endAdornment: !isMobile ? (
                  <InputAdornment position="end">
                    <Tooltip title={isOpen ? "Select time" : "Enable this day first to edit hours"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!isOpen}
                          onClick={openPicker}
                          sx={{
                            bgcolor: BRAND_CREAM,
                            border: "1px solid rgba(255,138,0,.25)",
                            opacity: isOpen ? 1 : 0.6,
                            "&:hover": {
                              bgcolor: BRAND_CREAM,
                            },
                          }}
                        >
                          <TimeIcon
                            sx={{
                              color: isOpen ? BRAND_ORANGE : "#BDBDBD",
                            }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />

            <input
              type="time"
              value={"00:00"}
              onChange={(e) => handleNativeChange(e, "from")}
              style={{
                position: "absolute",
                opacity: 0,
                width: 1,
                height: 1,
                pointerEvents: "none",
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Box
            data-time-container
            sx={{
              position: "relative",
            }}
          >
            <StyledTextField
              fullWidth
              disabled={!isOpen}
              label="End time"
              placeholder="17:00"
              value={toTime}
              error={isOpen && Boolean(toError)}
              helperText={isOpen ? toError || " " : " "}
              onChange={(e) => handleTextChange(e, "to")}
              inputProps={{
                maxLength: 5,
                inputMode: "numeric",
              }}
              InputProps={{
                endAdornment: !isMobile ? (
                  <InputAdornment position="end">
                    <Tooltip title={isOpen ? "Select time" : "Enable this day first to edit hours"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!isOpen}
                          onClick={openPicker}
                          sx={{
                            bgcolor: BRAND_CREAM,
                            border: "1px solid rgba(255,138,0,.25)",
                            opacity: isOpen ? 1 : 0.6,
                            "&:hover": {
                              bgcolor: BRAND_CREAM,
                            },
                          }}
                        >
                          <TimeIcon
                            sx={{
                              color: isOpen ? BRAND_ORANGE : "#BDBDBD",
                            }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />

            <input
              type="time"
              value={"23:59"}
              onChange={(e) => handleNativeChange(e, "to")}
              style={{
                position: "absolute",
                opacity: 0,
                width: 1,
                height: 1,
                pointerEvents: "none",
              }}
            />
          </Box>
        </Grid>
      </Grid>

      {day !== "Sunday" && (
        <Divider
          sx={{
            mt: 3,
            opacity: 0.25,
          }}
        />
      )}
    </Box>
  );
};

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

function normalizeCheckedState(checkedState) {
  const result = {};
  for (const [day, values] of Object.entries(checkedState)) {
    result[capitalizeFirstLetter(day)] = {
      isOpen: values.isOpen,
      from: values.from,
      to: values.to,
    };
  }
  return result;
}

export const Schedule = ({ updateComponentState, checkedState, setChanges = () => {}, onAvailabilityChange }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [hours, setHours] = useState(() => {
    if (checkedState && Object.keys(checkedState).length > 0) {
      return normalizeCheckedState(checkedState);
    }
    return defaultHours;
  });
  const isScheduleValid = Object.values(hours).every(({ isOpen, from, to }) => {
    if (!isOpen) return true;

    return !getStartTimeError(from) && !getEndTimeError(from, to);
  });
  useEffect(() => {
    onAvailabilityChange?.(hours, isScheduleValid);
  }, [hours, isScheduleValid]);
  useEffect(() => {
    if (!checkedState || Object.keys(checkedState).length === 0) return;

    const incoming = normalizeCheckedState(checkedState);

    setHours((prev) => {
      const isSame = JSON.stringify(prev) === JSON.stringify(incoming);
      return isSame ? prev : incoming;
    });
  }, [checkedState]);

  const handleSwitchTimeChange = useCallback(
    (day, type, event) => {
      setChanges(true);

      const value = type === "isOpen" ? event.target.checked : event.target.value;

      const updated = {
        ...hours,
        [day]: {
          ...hours[day],
          [type]: value,
        },
      };

      setHours(updated);

      dispatch(
        updateComponentState({
          availability: updated,
        }),
      );
    },
    [hours, dispatch, updateComponentState, onAvailabilityChange, setChanges],
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", md: 850 },
        mx: "auto",
        backgroundColor: "transparent",
        p: { xs: 1, md: 3 },
      }}
    >
      {Object.entries(hours).map(([day, { isOpen, from, to }]) => (
        <TimeSelector
          key={day}
          day={day}
          isOpen={isOpen}
          onChange={handleSwitchTimeChange}
          fromTime={from}
          toTime={to}
          isSmallScreen={isSmallScreen}
        />
      ))}
    </Box>
  );
};
